import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { getNewsList, getNewsCategorys,getNewsTags } from '../../services/news';

export default function NewsPage() {
 
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<{ id: string; name: string }>({ id: '', name: '全部' });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [fetchedNews, setFetchedNews] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState<'latest' | 'popular'>('latest');
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([{ id: '', name: '全部' }]);
  const itemsPerPage = 15;
  const [tags, setTags] = useState<string[]>([]);

  // 自动采集新闻
  const fetchLatestNews = async () => {
    setIsLoading(true);
    try {
      const latestNews = await newsService.fetchSportsNews();
      setFetchedNews(latestNews);
    } catch (error) {
      console.error('新闻采集失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 手动刷新采集新闻
  const handleRefreshNews = async () => {
    setIsLoading(true);
    try {
      // 清除缓存以获取最新数据
      newsService.clearCache();
      // 采集最新新闻
      const latestNews = await newsService.fetchLatestNews(20);
      if (latestNews.length > 0) {
        // 将新采集的新闻与现有新闻合并，避免重复
        const existingIds = new Set(fetchedNews.map(news => news.id));
        const uniqueNews = latestNews.filter(news => !existingIds.has(news.id));
        const updatedNews = [...uniqueNews, ...fetchedNews];
        setFetchedNews(updatedNews);
        console.log('🔄 手动刷新完成，新增', uniqueNews.length, '条新闻');
      } else {
        // 如果没有新的新闻，重新获取基础新闻
        const basicNews = await newsService.fetchSportsNews();
        setFetchedNews(basicNews);
      }
      // 重置到第一页
      setCurrentPage(1);
    } catch (error) {
      console.error('手动刷新新闻失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 页面加载时自动采集
  // 页面加载时自动采集和获取分类
  useEffect(() => {
    const fetchInit = async () => {
      setIsLoading(true);
      try {
        // 获取新闻分类
        const catRes = await getNewsCategorys();
        if (catRes && Array.isArray(catRes.data)) {
          setCategories([{ id: '', name: '全部' }, ...catRes.data]);
        }
        // 获取新闻列表
        const newsRes = await getNewsList({ category_id: '', keyword: '', page_size: 50, page: 1 });
        if (newsRes&& newsRes.status === 1 && Array.isArray(newsRes.data?.lists)) {
          setFetchedNews(newsRes.data.lists);
        }
        // 获取热门标签
        const tagsRes = await getNewsTags();
        if (tagsRes && tagsRes.status === 1 && Array.isArray(tagsRes.data)) {
          setTags(tagsRes.data.map((t: any) => t.name || t));
        }
      } catch (err) {
        console.error('新闻分类、新闻列表或标签获取失败:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInit();
  }, []);

   // 分类或搜索变动时请求新闻列表
  useEffect(() => {
    console.log('分类或搜索变动，重新获取新闻列表:', selectedCategory, searchQuery);
    const fetchNews = async () => {
      setIsLoading(true);
      try {
        const newsRes = await getNewsList({
          category_id: selectedCategory.id,
          keyword: searchQuery,
          page_size: 50,
          page: 1
        });
        if (newsRes && newsRes.status === 1 && Array.isArray(newsRes.data?.lists)) {
          setFetchedNews(newsRes.data.lists);
        }
      } catch (err) {
        console.error('新闻列表获取失败:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchNews();
  }, [selectedCategory, searchQuery]);

  // 获取当前显示的新闻源
  const getCurrentNewsSource = () => {
    return fetchedNews;
  };

  // 筛选新闻
  const getFilteredNews = () => {
    let newsSource = getCurrentNewsSource();
    
    // 按分类筛选
    if (selectedCategory.id !== '') {
      newsSource = newsSource.filter(news => news.category?.id == selectedCategory.id);
    }
    
    // 按搜索关键词筛选
    if (searchQuery) {
      newsSource = newsSource.filter(news => {
        const title = typeof news.title === 'string' ? news.title : '';
        const content = typeof news.summary === 'string' ? news.content : '';
        const category =
          typeof news.category === 'string'
            ? news.category
            : typeof news.category === 'object' && news.category?.name
            ? news.category.name
            : '';
        return (
          title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          content.toLowerCase().includes(searchQuery.toLowerCase()) ||
          category.toLowerCase().includes(searchQuery.toLowerCase())
        );
      });
    }
    
    // 排序
    if (sortBy === 'latest') {
      newsSource = [...newsSource].sort((a, b) => new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime());
    } else {
      newsSource = [...newsSource].sort((a, b) => {
        const aViews = parseInt(a.views.toString().replace(/[万K]/g, '')) || 0;
        const bViews = parseInt(b.views.toString().replace(/[万K]/g, '')) || 0;
        return bViews - aViews;
      });
    }
    
    return newsSource;
  };

  const filteredNews = getFilteredNews();
  const totalPages = Math.ceil(filteredNews.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentNews = filteredNews.slice(startIndex, startIndex + itemsPerPage);

  // 重置页码
  const resetPage = () => {
    setCurrentPage(1);
  };

  useEffect(() => {
    resetPage();
  }, [selectedCategory, searchQuery, sortBy]);

  // 格式化时间显示
  const formatTime = (timeStr?: string) => {
    if (!timeStr) return '';
    if (timeStr.includes('小时前') || timeStr.includes('分钟前') || timeStr.includes('刚刚')) {
      return timeStr;
    }
    return timeStr;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="py-4 md:py-6">
        <div className="container mx-auto px-4">
          {/* 页面标题 */}
          <div className="text-center mb-4">
            <h1 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
              体育新闻中心
            </h1>
            <p className="text-gray-600 text-sm">
              专业体育资讯，实时更新报道
            </p>
          </div>

          {/* 搜索和筛选 */}
          <div className="bg-white rounded-lg shadow-sm p-3 mb-4">
            <div className="flex flex-col lg:flex-row gap-2">
              {/* 搜索框 */}
              <div className="flex-1">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                    <i className="ri-search-line text-gray-400 text-xs"></i>
                  </div>
                  <input
                    type="text"
                    placeholder="搜索新闻标题、内容或分类..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') setSearchQuery(inputValue);
                    }}
                    onBlur={() => setSearchQuery(inputValue)}
                    className="w-full pl-7 pr-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-colors text-xs"
                  />
                  {inputValue && (
                    <button
                      onClick={() => {
                        setInputValue('');
                        setSearchQuery('');
                      }}
                      className="absolute inset-y-0 right-0 pr-2 flex items-center cursor-pointer"
                    >
                      <i className="ri-close-line text-gray-400 hover:text-gray-600 text-xs"></i>
                    </button>
                  )}
                </div>
              </div>

              {/* 排序选择 */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-600 whitespace-nowrap">排序:</span>
                <div className="flex bg-gray-100 rounded-lg p-0.5">
                  <button
                    onClick={() => setSortBy('latest')}
                    className={`px-2 py-0.5 rounded text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      sortBy === 'latest' 
                        ? 'bg-green-500 text-white shadow-sm' 
                        : 'text-gray-600 hover:text-green-600'
                    }`}
                  >
                    最新发布
                  </button>
                  <button
                    onClick={() => setSortBy('popular')}
                    className={`px-2 py-0.5 rounded text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      sortBy === 'popular' 
                        ? 'bg-green-500 text-white shadow-sm' 
                        : 'text-gray-600 hover:text-green-600'
                    }`}
                  >
                    热门阅读
                  </button>
                </div>
              </div>

              {/* 刷新按钮 */}
              <Button
                onClick={handleRefreshNews}
                disabled={isLoading}
                variant="outline"
                size="sm"
                className="whitespace-nowrap text-xs px-2 py-1"
              >
                <i className={`mr-0.5 text-xs ${isLoading ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'}`}></i>
                {isLoading ? '采集中' : '刷新采集'}
              </Button>
            </div>
          </div>

          {/* 分类标签 */}
          <div className="mb-4">
            <div className="flex flex-wrap gap-1">
              {categories.map((cat, idx) => (
                <button
                  key={cat.id || `cat-${idx}`}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setCurrentPage(1);
                  }}
                  className={`px-2 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    selectedCategory.id === cat.id
                      ? 'bg-green-500 text-white shadow-md'
                      : 'bg-white text-gray-600 hover:bg-green-50 hover:text-green-600 shadow-sm'
                  }`}
                >
                  {typeof cat.name === 'string' ? cat.name : ''}
                  {cat.id !== '' && (
                    <span className="ml-1 text-xs opacity-75">
                      ({getCurrentNewsSource().filter(news => news.category?.name === cat.name).length})
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* 结果统计 */}
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs text-gray-600">
              {searchQuery ? (
                <>找到 <span className="font-semibold text-gray-900">{filteredNews.length}</span> 条关于 "<span className="text-green-600">{searchQuery}</span>" 的结果</>
              ) : (
                <>共 <span className="font-semibold text-gray-900">{filteredNews.length}</span> 条{selectedCategory.name !== '全部' ? selectedCategory.name : ''}新闻</>
              )}
            </div>
            <div className="text-xs text-gray-500">
              第 {currentPage} 页，共 {totalPages} 页
            </div>
          </div>

          {/* 加载状态 */}
          {isLoading && (
            <div className="text-center py-6">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <i className="ri-loader-4-line text-green-500 text-sm animate-spin"></i>
              </div>
              <p className="text-green-600 font-medium text-xs">正在获取最新体育新闻...</p>
              <p className="text-gray-500 text-xs mt-1">请稍候，精彩内容即将呈现</p>
            </div>
          )}

          {/* 新闻列表 */}
          {!isLoading && currentNews.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <i className="ri-newspaper-line text-gray-400 text-lg"></i>
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">暂无相关新闻</h3>
              <p className="text-gray-500 text-xs mb-3">
                {searchQuery ? '尝试使用其他关键词搜索' : `暂时没有${selectedCategory.name}分类的新闻`}
              </p>
              {searchQuery && (
                <Button
                  onClick={() => setSearchQuery('')}
                  variant="outline"
                  size="sm"
                >
                  清空搜索条件
                </Button>
              )}
            </div>
          ) : !isLoading && (
            <>
              <div className="grid grid-cols-1 gap-2">
                {currentNews.map((news) => (
                  <article
                    key={news.id}
                    className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden cursor-pointer group border border-gray-100 hover:border-green-200"
                    onClick={() => navigate(`/newsDetail/${news.id}`, { state: { news } })}
                  >
                    <div className="p-3">
                      {/* 新闻头部信息 */}
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center space-x-1.5">
                          <span className="px-1.5 py-0.5 bg-green-100 text-green-600 text-xs font-medium rounded-full">
                            {typeof news.category === 'object' ? news.category?.name : (news.category || '')}
                          </span>
                          {fetchedNews.length > 0 && fetchedNews.some(fn => fn.id === news.id) && (
                            <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-xs font-medium rounded-full flex items-center">
                              <div className="w-1 h-1 bg-red-500 rounded-full mr-0.5 animate-pulse"></div>
                              实时
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 text-xs text-gray-500">
                          <div className="flex items-center">
                            <i className="ri-time-line mr-0.5 text-xs"></i>
                            <span>{formatTime(news.publishTime)}</span>
                          </div>
                          <div className="flex items-center">
                            <i className="ri-eye-line mr-0.5 text-xs"></i>
                            <span>{news.views} 阅读</span>
                          </div>
                        </div>
                      </div>

                      {/* 新闻标题 */}
                      <h2 className="font-bold text-gray-900 text-sm mb-1.5 group-hover:text-green-600 transition-colors leading-tight">
                        {news.title}
                      </h2>
                      
                      {/* 新闻摘要 */}
                      <p className="text-gray-600 text-xs mb-2 leading-relaxed line-clamp-2">
                        {news.summary}
                      </p>
                      
                      {/* 底部操作栏 */}
                      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                        <div className="flex items-center space-x-2">
                          <button className="flex items-center text-gray-500 hover:text-green-600 transition-colors text-xs cursor-pointer">
                            <i className="ri-thumb-up-line mr-0.5 text-xs"></i>
                            <span>点赞</span>
                          </button>
                          <button className="flex items-center text-gray-500 hover:text-blue-600 transition-colors text-xs cursor-pointer">
                            <i className="ri-chat-3-line mr-0.5 text-xs"></i>
                            <span>评论</span>
                          </button>
                          <button className="flex items-center text-gray-500 hover:text-purple-600 transition-colors text-xs cursor-pointer">
                            <i className="ri-share-line mr-0.5 text-xs"></i>
                            <span>分享</span>
                          </button>
                        </div>
                        <div className="flex items-center text-green-600 group-hover:text-green-700 transition-colors">
                          <span className="text-xs font-medium mr-0.5">阅读全文</span>
                          <i className="ri-arrow-right-line text-xs group-hover:translate-x-1 transition-transform"></i>
                        </div>
                      </div>
                    </div>

                    {/* 悬停效果条 */}
                    <div className="h-0.5 bg-gradient-to-r from-green-400 to-blue-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                  </article>
                ))}
              </div>

              {/* 分页控件 */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center mt-6 space-x-1">
                  {/* 上一页 */}
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-2 py-1 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-xs flex items-center whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line mr-0.5 text-xs"></i>
                    上一页
                  </button>

                  {/* 页码按钮 */}
                  <div className="flex items-center space-x-0.5">
                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                      let page;
                      if (totalPages <= 7) {
                        page = i + 1;
                      } else if (currentPage <= 4) {
                        page = i + 1;
                      } else if (currentPage >= totalPages - 3) {
                        page = totalPages - 6 + i;
                      } else {
                        page = currentPage - 3 + i;
                      }
                      
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-2 py-1 rounded-lg cursor-pointer text-xs whitespace-nowrap ${
                            currentPage === page
                              ? 'bg-green-500 text-white'
                              : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                  </div>

                  {/* 下一页 */}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-2 py-1 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-xs flex items-center whitespace-nowrap"
                  >
                    下一页
                    <i className="ri-arrow-right-line ml-0.5 text-xs"></i>
                  </button>
                </div>
              )}

              {/* 页面底部信息 */}
              <div className="text-center mt-4 text-xs text-gray-500">
                显示第 {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredNews.length)} 条，共 {filteredNews.length} 条结果
              </div>
            </>
          )}

          {/* 热门标签云 */}
          <div className="mt-6 bg-white rounded-lg shadow-sm p-3">
            <h3 className="font-bold text-gray-900 mb-2 flex items-center text-xs">
              <i className="ri-price-tag-3-line text-green-600 mr-1 text-xs"></i>
              热门标签
            </h3>
            <div className="flex flex-wrap gap-1">
              {tags.length > 0 ? (
                tags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setSearchQuery(tag)}
                    className="px-2 py-0.5 bg-gray-100 hover:bg-green-100 text-gray-700 hover:text-green-600 rounded-full text-xs cursor-pointer transition-colors"
                  >
                    #{tag}
                  </button>
                ))
              ) : (
                <span className="text-gray-400 text-xs">暂无标签</span>
              )}
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}